import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CategoryNavigation from "./CategoryNavigation";

import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import ViewKanban from "@mui/icons-material/ViewKanban";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import CakeIcon from "@material-ui/icons/Cake";
import PeopleIcon from "@material-ui/icons/People";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import BusinessIcon from "@material-ui/icons/Business";
import {
  AllInclusive,
  AttachFile,
  Description,
  DeviceHubOutlined,
  PhonelinkSetup,
} from "@material-ui/icons";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";


import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";
import { Campaign, ShapeLine, Webhook, AutoAwesome } from "@mui/icons-material";

import useCompanySettings from "../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  listItem: {
    minHeight: "48px",
    width: "calc(100% - 20px)",
    margin: "3px 10px",
    borderRadius: 12,
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "#F2F3FF" : "rgba(119,128,255,.10)",
    },
    "&.active": {
      backgroundColor: theme.mode === "light" ? "#EAECFF" : "rgba(119,128,255,.16)",
    },
    "&.active $listItemText": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
    // Remove margin personalizada para manter alinhamento original
    "&:hover $iconHoverActive": {
      backgroundColor: "#4450FF",
      color: "#fff",
    },
    "&:hover $listItemText": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontWeight: 600,
    },
    // Transição suave
    transition: "background-color 0.18s ease, color 0.18s ease",
  },

  listItemText: {
    fontSize: "13.5px",
    color: theme.mode === "light" ? "#4E5264" : "#E4E6EF",
    transition: "color 0.18s ease",
    fontWeight: 600,
    "& .MuiTypography-root": {
      fontFamily: "'Inter', sans-serif",
    }
  },

  avatarActive: {
    backgroundColor: "transparent",
  },

  avatarHover: {
    backgroundColor: "transparent",
  },

  iconHoverActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 10,
    height: 34,
    width: 34,
    backgroundColor:
      theme.mode === "light"
        ? "#F2F3F7"
        : "rgba(27, 36, 49, 0.9)",
    color: theme.mode === "light" ? "#1b1b1b" : "#FFF",
    transition: "background-color 0.18s ease, color 0.18s ease",
    "&:hover, &.active": {
      backgroundColor: theme.palette.primary.main, // Usa cor do tema
      color: "#fff",
      boxShadow: "0 6px 16px rgba(68,80,255,.22)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.4rem", // Mantém tamanho original
      transition: "color 0.18s ease",
    }
  },

  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ef4444",
      color: "#fff",
      fontSize: "0.75rem",
      fontWeight: 600,
      animation: "$pulse 2s infinite",
    }
  },

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.7,
    }
  },

  // Melhorias para submenus mantendo estrutura original
  submenuContainer: {
    backgroundColor: theme.mode === "light"
      ? "#F8F8FB"
      : "rgba(255, 255, 255, 0.02)",
  },

  // Tooltip melhorado
  customTooltip: {
    backgroundColor: theme.mode === "light" ? "#1e293b" : "#374151",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    borderRadius: "10px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    "& .MuiTooltip-arrow": {
      color: theme.mode === "light" ? "#1e293b" : "#374151",
    }
  },

  // Versão com destaque sutil
  versionContainer: {
    textAlign: "center",
    padding: "10px",
    color: theme.palette.primary.main, // Usa cor do tema
    fontSize: "12px",
    fontWeight: "bold",
    borderTop: `1px solid ${theme.mode === "light" ? "#f0f0f0" : "#273142"}`,
    marginTop: "auto",
  },

  // Seções de administração com destaque sutil
  adminSection: {
    "& .MuiListSubheader-root": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }
  },

  // Efeitos suaves para expand/collapse
  expandIcon: {
    transition: "transform 0.3s ease",
    color: theme.palette.primary.main, // Usa cor do tema
    "&.expanded": {
      transform: "rotate(180deg)",
    }
  },

  // Menu container com melhorias sutis
  menuContainer: {
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.mode === "light"
        ? "#CDD0DC"
        : "rgba(255, 255, 255, 0.1)",
      borderRadius: "8px",
      "&:hover": {
        background: theme.mode === "light"
          ? "rgba(0, 0, 0, 0.2)"
          : "rgba(255, 255, 255, 0.2)",
      }
    },
  },

  // Estado ativo melhorado mantendo funcionalidade original
  activeItem: {
    "& $iconHoverActive": {
      backgroundColor: theme.palette.primary.main, // Usa cor do tema
      color: "#fff",
    },
    "& $listItemText": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontWeight: 700,
    }
  }
}));

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, show } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip title={primary} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );

  // if (show === false) return null; // DESLIGADO: feature flags por plano (revertido a pedido)

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem button component={renderLink} className={`${classes.listItem} ${isActive ? "active" : ""}`}>
          {icon ? (
            <ListItemIcon>
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  <Avatar
                    className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                      }`}
                  >
                    {icon}
                  </Avatar>
                </Badge>
              ) : (
                <Avatar
                  className={`${classes.iconHoverActive} ${isActive ? "active" : ""
                    }`}
                >
                  {icon}
                </Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={
              <Typography className={classes.listItemText}>
                {primary}
              </Typography>
            }
          />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const StandardMainListItems = ({ collapsed, drawerClose }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);

  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showQuickMessages, setShowQuickMessages] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showTodoList, setShowTodoList] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showFlowBuilder, setShowFlowBuilder] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [showBirthday, setShowBirthday] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [showTemplatesMeta, setShowTemplatesMeta] = useState(false);
  const [showFinanceiro, setShowFinanceiro] = useState(false);
  const [showCharges, setShowCharges] = useState(false);
  const [showFinance, setShowFinance] = useState(false);
  const [showAffiliates, setShowAffiliates] = useState(false);
  const [showSupervisors, setShowSupervisors] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [showMetaIntegration, setShowMetaIntegration] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const { list } = useHelps(); // INSERIR
  const [hasHelps, setHasHelps] = useState(false);


  const { get: getSetting } = useCompanySettings();
  const [showWallets, setShowWallets] = useState(false);

  useEffect(() => {
    // INSERIR ESSE EFFECT INTEIRO
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting(
          {
            "column": "DirectTicketsToWallets"
          }
        );

        setShowWallets(setting.DirectTicketsToWallets);

      } catch (err) {
        toastError(err);
      }
    }

    fetchSettings();
  }, [setShowWallets]);

  const isManagementActive = location.pathname === "/";

  const isCampaignRouteActive =
    location.pathname === "/campaigns";

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      if (!companyId) return;
      const planConfigs = await getPlanCompany(undefined, companyId);

      const isSuper = user.super;
      setShowCampaigns(isSuper || planConfigs.plan.useCampaigns);
      setShowKanban(isSuper || planConfigs.plan.useKanban);
      setShowOpenAi(isSuper || planConfigs.plan.useOpenAi);
      setShowIntegrations(isSuper || planConfigs.plan.useIntegrations);
      setShowSchedules(isSuper || planConfigs.plan.useSchedules);
      setShowInternalChat(isSuper || planConfigs.plan.useInternalChat);
      setShowExternalApi(isSuper || planConfigs.plan.useExternalApi);
      setShowWavoipCall(isSuper || planConfigs.plan.wavoip);
      setShowQuickMessages(isSuper || planConfigs.plan.useQuickMessages);
      setShowTags(isSuper || planConfigs.plan.useTags);
      setShowTodoList(isSuper || planConfigs.plan.useTodoList);
      setShowAnnouncements(isSuper || planConfigs.plan.useAnnouncements);
      setShowFlowBuilder(true); // Editor de fluxos disponivel para todas as empresas
      setShowFollowup(isSuper || planConfigs.plan.useFollowup);
      setShowBirthday(isSuper || planConfigs.plan.useBirthday);
      setShowFiles(isSuper || planConfigs.plan.useFiles);
      setShowPrompts(isSuper || planConfigs.plan.usePrompts);
      setShowTemplatesMeta(isSuper || planConfigs.plan.useTemplatesMeta);
      setShowFinanceiro(isSuper || planConfigs.plan.useFinanceiro);
      setShowCharges(isSuper || planConfigs.plan.useCharges);
      setShowFinance(isSuper || planConfigs.plan.useFinance);
      setShowAffiliates(isSuper || planConfigs.plan.useAffiliates);
      setShowSupervisors(isSuper || planConfigs.plan.useSupervisors);
      setShowProducts(isSuper || planConfigs.plan.useProducts);
      setShowStore(isSuper || planConfigs.plan.useStore);
      setShowAgenda(isSuper || planConfigs.plan.useAgenda);
      setShowMetaIntegration(isSuper || planConfigs.plan.useMetaIntegration);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === 'function') {
      const companyId = user.companyId;

      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      const eventName = `company-${companyId}-chat`;
      console.log('Registrando listener para:', eventName);

      socket.on(eventName, onCompanyChatMainListItems);

      return () => {
        if (socket && typeof socket.off === 'function') {
          console.log('Removendo listener para:', eventName);
          socket.off(eventName, onCompanyChatMainListItems);
        }
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  // useEffect(() => {
  //   if (localStorage.getItem("cshow")) {
  //     setShowCampaigns(true);
  //   }
  // }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const canSeeAdminNavigation = user.profile === "admin" || user.allowConnections === "enabled";
  const isAdmin = user.profile === "admin";
  const isSuper = user.super;
  const link = (to, label, icon, extra = {}) => ({ to, label, icon, ...extra });
  const group = (label, icon, children) => ({ label, icon, children });
  const categories = [
    {
      id: "overview",
      label: "Visão Geral & Operação",
      icon: <DashboardOutlinedIcon />,
      children: [
        ...(user.showDashboard === "enabled" ? [link("/", "Dashboard", <DashboardOutlinedIcon />)] : []),
        link("/tickets", i18n.t("mainDrawer.listItems.tickets"), <WhatsAppIcon />),
        ...(showKanban ? [link("/kanban", i18n.t("mainDrawer.listItems.kanban"), <ViewKanban />)] : []),
        ...((showSchedules || isAdmin) ? [link("/agenda", "Agenda", <EventAvailableIcon />, { show: showAgenda })] : []),
        ...(showInternalChat ? [link("/chats", i18n.t("mainDrawer.listItems.chats"),
          <Badge color="secondary" variant="dot" invisible={invisible}><ForumIcon /></Badge>)] : []),
      ],
    },
    {
      id: "crm",
      label: "CRM & Base de Clientes",
      icon: <ContactPhoneOutlinedIcon />,
      children: [
        ...(user.showContacts === "enabled" ? [link("/contacts", i18n.t("mainDrawer.listItems.contacts"), <ContactPhoneOutlinedIcon />)] : []),
        ...(isAdmin || isSuper ? [link("/groups", "Grupos", <GroupOutlinedIcon />)] : []),
        link("/tags", i18n.t("mainDrawer.listItems.tags"), <LocalOfferIcon />, { show: showTags }),
        ...(canSeeAdminNavigation && isAdmin ? [link("/birthday-settings", "Aniversariantes", <CakeIcon />, { show: showBirthday })] : []),
      ],
    },
    {
      id: "automation",
      label: "Automação, IA & Marketing",
      icon: <Campaign />,
      children: [
        ...(user?.showCampaign === "enabled" && showCampaigns ? [link("/campaigns", "Campanhas", <EventAvailableIcon />)] : []),
        ...((user.showFlow === "enabled" || (canSeeAdminNavigation && isAdmin)) ? [group("Automações", <Webhook />, [
          ...(user.showFlow === "enabled" ? [link("/phrase-lists", "Fluxo de Campanha", <EventAvailableIcon />, { show: showFlowBuilder })] : []),
          ...(user.showFlow === "enabled" ? [link("/flowbuilders", "Fluxo de Conversa", <ShapeLine />, { show: showFlowBuilder })] : []),
          ...(canSeeAdminNavigation && isAdmin ? [link("/queues", "Filas & Chatbot", <AccountTreeOutlinedIcon />)] : []),
        ])] : []),
        ...((user.showFlow === "enabled" || (showOpenAi && canSeeAdminNavigation && isAdmin)) ? [group("Inteligência Artificial", <AutoAwesome />, [
          ...(user.showFlow === "enabled" ? [link("/ai-agents", "Agente de IA", <AutoAwesome style={{ color: "#F97316" }} />)] : []),
          ...(showOpenAi && canSeeAdminNavigation && isAdmin ? [link("/prompts", "Talk.AI", <AllInclusive />)] : []),
        ])] : []),
        group("Modelos e Respostas", <Description />, [
          link("/quick-messages", i18n.t("mainDrawer.listItems.quickMessages"), <FlashOnIcon />, { show: showQuickMessages }),
          link("/template-manager", "Templates Meta", <Description />, { show: showTemplatesMeta }),
          ...(user.showFlow === "enabled" ? [link("/plugins/floup", "Follow UP (Templates)", <Webhook />, { show: showFollowup })] : []),
        ]),
      ],
    },
    {
      id: "sales",
      label: "Vendas & Financeiro",
      icon: <AccountBalanceWalletIcon />,
      children: [
        ...(canSeeAdminNavigation && isAdmin ? [link("/financeiro", i18n.t("mainDrawer.listItems.financeiro"), <LocalAtmIcon />, { show: showFinanceiro })] : []),
        ...(canSeeAdminNavigation && isAdmin ? [link("/finance", "Fluxo de Caixa", <LocalAtmIcon />, { show: showFinance })] : []),
        ...(canSeeAdminNavigation && isAdmin ? [link("/charges", "Cobranças", <LocalAtmIcon />, { show: showCharges })] : []),
        ...(canSeeAdminNavigation && (isAdmin || isSuper) ? [group("Catálogo & Loja", <LocalAtmIcon />, [
          ...(isAdmin ? [link("/loja-config", "Loja Online", <LocalAtmIcon />, { show: showStore })] : []),
          ...(isAdmin ? [link("/produtos-servicos", "Produtos e Serviços", <LocalAtmIcon />, { show: showProducts })] : []),
          ...(isAdmin ? [link("/relatorio-produtos", "Relatório de Produtos", <LocalAtmIcon />, { show: showProducts })] : []),
        ])] : []),
        ...(canSeeAdminNavigation && isSuper ? [link("/affiliates", "Afiliados", <PeopleAltOutlinedIcon />, { show: showAffiliates })] : []),
      ],
    },
    {
      id: "administration",
      label: "Administração & Configurações",
      icon: <SettingsOutlinedIcon />,
      children: [
        ...(canSeeAdminNavigation ? [group("Gestão de Equipe", <PeopleIcon />, [
          ...(isAdmin ? [link("/users", "Usuários", <PeopleAltOutlinedIcon />)] : []),
          ...(isAdmin ? [link("/management", "Supervisão e Gerência", <SupervisorAccountIcon />)] : []),
          ...(isSuper ? [link("/supervisors", "Supervisores", <BusinessIcon />, { show: showSupervisors })] : []),
        ])] : []),
        ...(canSeeAdminNavigation ? [group("Conexões & API", <SyncAltIcon />, [
          ...((isAdmin || user.allowConnections === "enabled") ? [link("/connections", "Conexões", <SyncAltIcon />, { showBadge: connectionWarning })] : []),
          ...(isSuper ? [link("/allConnections", "Gerenciar Conexões", <PhonelinkSetup />)] : []),
          ...(showIntegrations && isAdmin ? [link("/queue-integration", "Integrações", <DeviceHubOutlined />)] : []),
          ...(isAdmin ? [link("/integracao-meta", "Integração Meta", <LocalAtmIcon />, { show: showMetaIntegration })] : []),
          ...(showExternalApi && isAdmin ? [link("/messages-api", "API", <CodeRoundedIcon />)] : []),
        ])] : []),
        ...(canSeeAdminNavigation ? [group("Sistema", <SettingsOutlinedIcon />, [
          ...(isAdmin ? [link("/settings", "Configurações Gerais", <SettingsOutlinedIcon />)] : []),
          ...(isSuper ? [link("/companies", "Empresas", <BusinessIcon />)] : []),
          ...(isSuper ? [link("/announcements", "Informativos", <AnnouncementIcon />)] : []),
          ...(isAdmin ? [link("/files", "Lista de Arquivos", <AttachFile />)] : []),
        ])] : []),
      ],
    },
    {
      id: "support",
      label: "Suporte",
      icon: <HelpOutlineIcon />,
      children: hasHelps ? [link("/helps", "Ajuda", <HelpOutlineIcon />)] : [],
    },
  ];
  const pruneEmptyGroups = (entries) => entries
    .map((entry) => entry.children
      ? { ...entry, children: pruneEmptyGroups(entry.children) }
      : entry)
    .filter((entry) => entry.to || entry.children.length > 0);
  const visibleCategories = categories
    .map((category) => ({ ...category, children: pruneEmptyGroups(category.children) }))
    .filter((category) => category.children.length > 0);

  return (
    <div
      onClick={(event) => {
        if (drawerClose && event.target.closest("a")) {
          drawerClose();
        }
      }}
    >
      <CategoryNavigation
        categories={visibleCategories}
        collapsed={collapsed}
        drawerClose={drawerClose}
        itemClasses={classes}
        renderLink={(item) => (
          <ListItemLink
            to={item.to}
            primary={item.label}
            icon={item.icon}
            show={item.show}
            showBadge={item.showBadge}
            tooltip={collapsed}
          />
        )}
      />      {!collapsed && (
        <React.Fragment>
          <Divider />
          <Typography
            style={{
              fontSize: "12px",
              padding: "10px",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            {`${version}`}
          </Typography>
        </React.Fragment>
      )}
    </div>
  );
};

const ManagerNavigation = ({ collapsed, drawerClose }) => (
  <div onClick={event => { if (drawerClose && event.target.closest("a")) drawerClose(); }}>
    {!collapsed && <ListSubheader component="div">Supervisão e gerência</ListSubheader>}
    <ListItemLink
      to="/management"
      primary="Conversas supervisionadas"
      icon={<SupervisorAccountIcon />}
      tooltip={collapsed}
    />
    <ListItemLink
      to="/dashboard?view=ai-reports"
      primary="Relatório de atendimentos"
      icon={<DashboardOutlinedIcon />}
      tooltip={collapsed}
    />
  </div>
);

const MainListItems = props => {
  const { user } = useContext(AuthContext);
  return user?.profile === "manager"
    ? <ManagerNavigation {...props} />
    : <StandardMainListItems {...props} />;
};

export default MainListItems;
